/**
 * @fileoverview Polls the Task Tracker matrix while newly added participants are
 * still being processed by the backend.
 *
 * When a student is added to a project the backend links them synchronously but
 * regenerates their assessment structure asynchronously (UpdateAssessmentsJob).
 * The matrix reports `processing: true` until that job finishes. This hook
 * silently re-fetches on an interval so the row fills in on its own, without the
 * teacher having to refresh — and stops once processing clears or the ceiling is
 * reached (so a stuck job never polls forever).
 */

import { useEffect, useRef } from 'react'

const POLL_INTERVAL_MS = 4000
const POLL_MAX_ATTEMPTS = 15 // ~1 min ceiling

export function useProcessingPoll(processing: boolean, onPoll: () => void): void {
  const onPollRef = useRef(onPoll)
  onPollRef.current = onPoll

  useEffect(() => {
    if (!processing) return

    let attempts = 0
    const timer = setInterval(() => {
      attempts += 1
      if (attempts > POLL_MAX_ATTEMPTS) {
        clearInterval(timer)
        return
      }
      onPollRef.current()
    }, POLL_INTERVAL_MS)

    return () => clearInterval(timer)
  }, [processing])
}
